import * as React from 'react';
import classnames from 'classnames';

import ProposalTileHeader from './components/header/ProposalTileHeader';
import InfoSimple from './components/info-simple/InfoSimple';
import InfoAdvanced from './components/info-advanced/InfoAdvanced';
import ProposalTileFooter from './components/footer/ProposalTileFooter';
import ApprovalModeFooter from './components/footer-approval/ApprovalModeFooter';
import NonApprovalModeFooter from './components/footer-non-aproval/NonApprovalModeFooter';
import EmptyChannel from './components/empty-channel/EmptyChannel';
import ProposalImageGrid from '../image-grid-v2/ProposalImageGrid';
import Spinner from 'components/spinner/Spinner';

import {
  canAutofillPosts,
  canResetPosts,
  collectActiveChannels
} from 'modules/proposal/components/proposal-tile-v2/utils/tile-utils';
import {
  DEFAULT_LIST_CHANNELS,
  DEFAULT_MEMBER_CUSTOM_CHANNELS,
  POSTS_AMOUNT_INSTAGRAM_LEGACY
} from 'modules/proposal/utils/creator-proposal-utils';

import type {
  ListContextMenu,
  ProposalCreatorProfile,
  ProposalImage,
  ProposalListMemberStatus
} from 'types';
import type {
  ProposalTileChannelType,
  ProposalTileChannelTypeOptions,
  ProposalTileNotes,
  ProposalTileType
} from 'modules/proposal/types/ProposalTile.type';

import './proposal-tile.scss';

type Props = {
  // generic
  isForBrand: boolean,
  template: ProposalTileType,
  creatorProfile: ProposalCreatorProfile,
  isLoading?: boolean,

  // context menu
  contextMenuProps?: ListContextMenu,
  canDelete?: boolean,
  onDelete?: () => void,

  // channels
  canHaveChannelsSelector?: boolean,
  customChannels?: Array<ProposalTileChannelType>,
  channelOptions: Array<ProposalTileChannelTypeOptions>,
  onSelectedChannelChange?: (channels: Array<ProposalTileChannelType>) => void,

  // notes
  notes?: ProposalTileNotes,
  onNoteUpdate: (note?: string | null) => void,

  // favourite
  onToggleFavorite?: () => void,
  isFavorite: boolean,

  // status
  status: ProposalListMemberStatus,
  onApprove?: () => void,
  onReject?: () => void,
  onStatusReset?: () => void,

  // media
  posts: Array<ProposalImage>,
  postsTikTok: Array<ProposalImage>,
  onMediaSet?: (image: ProposalImage | null, imageIndex: number, channelType: ProposalTileChannelType) => void,
  onMediaReset?: () => void,
  onAutofill?: () => void
};

const ProposalTile: React.FunctionComponent<Props> = (
  {
    template,
    isForBrand,
    creatorProfile,
    isLoading,

    contextMenuProps,
    canDelete,
    onDelete,

    canHaveChannelsSelector = false,
    customChannels = DEFAULT_MEMBER_CUSTOM_CHANNELS,
    channelOptions = DEFAULT_LIST_CHANNELS,
    onSelectedChannelChange = () => {},

    notes,
    onNoteUpdate,

    isFavorite,
    onToggleFavorite,

    status,
    onReject,
    onApprove,
    onStatusReset,

    posts,
    postsTikTok,
    onMediaSet = () => {},
    onMediaReset,
    onAutofill
  }: Props
): JSX.Element => {
  // collect available channels and match them with selected channels
  const channels = collectActiveChannels(creatorProfile, channelOptions, customChannels, channelOptions.includes('custom'));

  const nonEmptyIGPosts = posts.filter((entry) => entry?.imageUrl).length;
  const nonEmptyTTPosts = postsTikTok.filter((entry) => entry?.imageUrl).length;

  const canSetMedia = !isForBrand && status !== 'approved' && status !== 'declined';
  const canAddToFavorite = isForBrand && status === 'approved';
  const isInApprovalMode = isForBrand && status === 'pending';

  const displayLegacyWarning = channels.length === 1
    && channels[0].channelType === 'instagram'
    && nonEmptyIGPosts === POSTS_AMOUNT_INSTAGRAM_LEGACY
    && !isForBrand
    && status === 'pending';

  const loaderClasses = classnames({
    'proposal-tile__image-grid-loader': channels.length !== 0,
    'proposal-tile__image-grid-loader--single-channel': channels.length === 1
  });

  return (
    <section className="proposal-tile">
      <ProposalTileHeader
        isForBrand={isForBrand}
        creatorProfile={creatorProfile}
        showWarning={displayLegacyWarning}
      />
      <div className="proposal-tile__content">
        {
          isLoading && (
            <div className={loaderClasses}>
              <Spinner
                position="inline"
                size="small"
                color="light"
              />
            </div>
          )
        }
        {
          channels.length === 0 && <EmptyChannel />
        }
        {
          channels.map(({ channelId, channelType }) => (
            <div
              key={channelId}
              className="proposal-tile__content-section"
            >
              <div className="proposal-tile__content-metrics">
                {template === 'basic' && (
                  <InfoSimple
                    channelType={channelType}
                    creatorProfile={creatorProfile}
                  />
                )}
                {template === 'advanced' && (
                  <InfoAdvanced
                    channelType={channelType}
                    creatorProfile={creatorProfile}
                  />
                )}
              </div>
              <ProposalImageGrid
                channelType={channelType}
                canSetMedia={canSetMedia}
                profileId={creatorProfile.id}
                channelId={channelId}
                posts={(channelType === 'instagram') ? posts : postsTikTok}
                onMediaSet={onMediaSet}
              />
            </div>
          ))
        }
      </div>
      <ProposalTileFooter
        isForBrand={isForBrand}
        notes={notes}
        onNoteUpdate={onNoteUpdate}
      >
        {isInApprovalMode && (
          <ApprovalModeFooter
            onReject={onReject}
            onApprove={onApprove}
            hideContextMenu={isForBrand}
            canDelete={canDelete}
            onDelete={onDelete}
            creatorProfile={creatorProfile}
            contextMenuProps={contextMenuProps}
          />
        )}

        {!isInApprovalMode && (
          <NonApprovalModeFooter
            canSetCustomChannels={canHaveChannelsSelector && channelOptions.includes('custom')}
            customChannels={customChannels}
            onCustomChannelChange={onSelectedChannelChange}
            canAutofillPosts={canSetMedia && canAutofillPosts(channels, nonEmptyTTPosts, nonEmptyIGPosts)}
            canResetPosts={canResetPosts(channels, nonEmptyTTPosts, nonEmptyIGPosts)}
            canSetMedia={canSetMedia}
            status={status}
            hideContextMenu={isForBrand}
            canDelete={canDelete}
            creatorProfile={creatorProfile}
            contextMenuProps={contextMenuProps}
            canAddToFavorite={canAddToFavorite}
            onToggleFavorite={onToggleFavorite}
            isFavorite={isFavorite}
            onDelete={onDelete}
            onMediaReset={onMediaReset}
            onAutofill={onAutofill}
            onStatusReset={onStatusReset}
          />
        )}
      </ProposalTileFooter>
    </section>
  );
};

export default ProposalTile;
